import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { t } from "../i18n";
import styles from "./GameCommunityPage.module.css";
import PostingList from "./PostingList";
import SortBy from "./SortBy";
import gamesData from "./gamesData";
import "./PostPage.css";
import "./Posts.css";

const GameCommunityPage = () => {
  const location = useLocation();
  const game = location.state?.game || {};

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 750);

  const [visibleCount, setVisibleCount] = useState(22);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [filter1, setFilter1] = useState("");
  const [filter2, setFilter2] = useState("");
  const [filter3, setFilter3] = useState("");
  const [filter4, setFilter4] = useState("All");

  const [favoriteGameNames, setFavoriteGameNames] = useState([]);
  const [friendPosts, setFriendPosts] = useState([]);

  const clearFilters = () => {
    setFilter1("");
    setFilter2("");
    setFilter3("");
    //setFilter4("All");
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 750);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      if (
        !isLoadingMore &&
        window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 50 &&
        visibleCount < posts.length
      ) {
        setIsLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((prev) => Math.min(prev + 15, posts.length));
          setIsLoadingMore(false);
        }, 1000);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [posts.length, isMobile, isLoadingMore, visibleCount]);

  const fetchPosts = useCallback(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_PATH}/posts`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const postsArray = Array.isArray(data[0]) ? data[0] : data;

        const gamePosts = postsArray.filter((post) =>
          post?.attributes?.tags?.some(
            (tag) => tag?.trim().toLowerCase() === game.name?.trim().toLowerCase()
          )
        );

        setPosts(gamePosts);
      })
      .catch((error) => {
        console.error("Error fetching posts:", error);
      })
      .finally(() => setLoading(false));
  }, [game.name]);

  useEffect(() => {
    fetchPosts();

    // Fetch user favorite games
    const userToken = sessionStorage.getItem("token");
    const userId = sessionStorage.getItem("user");
    fetch(`${process.env.REACT_APP_API_PATH}/users/${userId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    })
      .then((res) => res.json())
      .then((result) => {
        const favorites = result.attributes?.favoriteGames || [];
        const names = favorites
          .map((favoriteId) => {
            const game = Object.values(gamesData).find((g) => g.id === favoriteId);
            return game ? Object.keys(gamesData).find(key => gamesData[key].id === favoriteId) : null;
          })
          .filter(Boolean);
        setFavoriteGameNames(names);
      })
      .catch(console.error);
  }, [fetchPosts]);

  useEffect(() => {
    if (filter4 !== "Friends") {
      setFriendPosts([]);
      return;
    }

    const fetchFriendsPosts = async () => {
      try {
        const userId = parseInt(sessionStorage.getItem("user"));
        const token = sessionStorage.getItem("token");

        const response = await fetch(`${process.env.REACT_APP_API_PATH}/connections?anyUserID=${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const connections = await response.json();
        const activeFriends = connections[0].filter((conn) => conn.attributes.status === "active");

        const posts = [];
        for (const friend of activeFriends) {
          const friendId = friend.fromUserID === userId ? friend.toUserID : friend.fromUserID;
          const postsResponse = await fetch(`${process.env.REACT_APP_API_PATH}/posts?authorID=${friendId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const friendPosts = await postsResponse.json();
          if (friendPosts[0]) posts.push(...friendPosts[0]);
        }

        setFriendPosts(posts);
      } catch (error) {
        console.error("Error fetching friend's posts:", error);
      }
    };

    fetchFriendsPosts();
  }, [filter4]);

  const filteredPosts = posts.filter((post) => {
    const tags = post.attributes?.tags || [];
    const title = post.title?.toLowerCase() || "";
    const body = post.body?.toLowerCase() || "";
    const username = post.author?.attributes?.username?.toLowerCase() || "";

    const textMatch =
      !filter1 ||
      title.includes(filter1.toLowerCase()) ||
      body.includes(filter1.toLowerCase()) ||
      username.includes(filter1.toLowerCase());

    const filter2Match = !filter2 || tags.includes(filter2);
    const filter3Match = !filter3 || tags.includes(filter3);

    const filter4Match =
      filter4 === "Friends"
        ? friendPosts.some((friendPost) => friendPost.id === post.id)
        : filter4 === "Favorite Games"
        ? favoriteGameNames.some((gameName) =>
            tags.some((tag) => tag?.toLowerCase() === gameName?.toLowerCase())
          )
        : true;

    return textMatch && filter2Match && filter3Match && filter4Match;
  });

  return (
    <div>
      {isMobile ? (
        <div className={styles.mobileContentWrapper}>
          <div className={styles.mobilePostsSection}>
            <SortBy
              filter1={filter1}
              setFilter1={setFilter1}
              filter2={filter2}
              setFilter2={setFilter2}
              filter3={filter3}
              setFilter3={setFilter3}
              filter4={filter4}
              setFilter4={setFilter4}
              clearFilters={clearFilters}
              isMobile={isMobile}
            />
            <div className="posts-container">
              {loading ? (
                <p>{t("loading_posts")}</p>
              ) : filteredPosts.length === 0 ? (
                <p>{t("no_posts_yet")}</p>
              ) : (
                <PostingList posts={filteredPosts} loadPosts={fetchPosts} type="postlist" />
              )}
            </div>
          </div>
          <div className={styles.mobileGameInfo}>
            <div className={styles.gameImage}>
              <img src={game.carouselImage} alt={game.name} />
            </div>
            <div className={styles.gameDetails}>
              <h2>{game.name}</h2>
              <h4>Genre: {game.genre}</h4>
              <p>{game.about}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.contentWrapper}>
          <div className={styles.leftSection}>
            <SortBy
              filter1={filter1}
              setFilter1={setFilter1}
              filter2={filter2}
              setFilter2={setFilter2}
              filter3={filter3}
              setFilter3={setFilter3}
              filter4={filter4}
              setFilter4={setFilter4}
              clearFilters={clearFilters}
              isMobile={isMobile}
            />
            <div className="posts-container">
              {loading ? (
                <p>{t("loading_posts")}</p>
              ) : filteredPosts.length === 0 ? (
                <p>{t("no_posts_yet")}</p>
              ) : (
                <>
                  <PostingList
                    posts={filteredPosts.slice(0, visibleCount)}
                    loadPosts={fetchPosts}
                    type="postlist"
                  />
                  {isLoadingMore && (
                    <div className="loading-spinner">{t("loading_more_posts")}</div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className={styles.gameInfoContainer}>
            <div className={styles.gameImage}>
              <img src={game.carouselImage} alt={game.name} />
            </div>
            <div className={styles.gameDetails}>
              <h2>{game.name}</h2>
              <h4>Genre: {game.genre}</h4>
              <p>{game.about}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameCommunityPage;
